/**
 * PATH: packages/ui/src/kanban/cardColors.ts
 * FILE_ID: UI-KANBAN-CARD-COLORS-001
 * PURPOSE: The per-card colour palette + pure helpers that turn a stored colour
 *   TOKEN (Card.color) into the swatch hue shown in the picker and the soft,
 *   mode-aware tint the card composites over its surface.
 * WHY: FEAT-2026-06-22-KANBAN-CARD-COLOR — operator wants a Trello-style colour
 *   picker on the right edge of a card that tints the card background. We persist
 *   only a short token on the card; this module is the SINGLE source of truth
 *   that turns that token into pixels, so light/dark rendering stays themeable
 *   client-side and the backend never deals in hex.
 * FLOW:
 *   ┌──────────────┐   ┌────────────────────┐   ┌───────────────────────┐
 *   │ token 'blue' │ → │ resolveCardColor*  │ → │ { background, swatch } │ → card
 *   └──────────────┘   └────────────────────┘   └───────────────────────┘
 * DEPENDENCIES: none (pure I→O — trivially unit-testable).
 * RELATED:
 *   - ./components/KanbanCardLabelPicker.tsx — the picker UI (labels + swatches)
 *   - ./components/KanbanCardHoverMenu.tsx — the right-edge hover menu host
 *   - ./components/KanbanCard.tsx — applies the tint to the card background
 *   - ../types Card.color / ./api CardCreate.color — the persisted token
 */

export interface CardColorOption {
  /** Stored token (persisted as Card.color). '' (no colour) is implicit and
   *  NOT part of this list — the picker renders a dedicated "clear" affordance. */
  token: string
  /** Human label for the swatch tooltip / aria-label. */
  label: string
  /** The saturated hue shown as the picker swatch — a real, recognisable colour
   *  (the card BACKGROUND is a softened tint of this, see resolveCardColorStyle). */
  swatch: string
}

/**
 * The curated palette. Eight hues — enough to categorise a board at a glance,
 * few enough to stay a neat single row. Order is the visual spectrum so the
 * picker reads like a colour bar.
 */
export const CARD_COLOR_OPTIONS: readonly CardColorOption[] = [
  { token: 'red', label: 'Red', swatch: '#ef4444' },
  { token: 'orange', label: 'Orange', swatch: '#f97316' },
  { token: 'amber', label: 'Amber', swatch: '#f59e0b' },
  { token: 'green', label: 'Green', swatch: '#22c55e' },
  { token: 'teal', label: 'Teal', swatch: '#14b8a6' },
  { token: 'blue', label: 'Blue', swatch: '#3b82f6' },
  { token: 'purple', label: 'Purple', swatch: '#a855f7' },
  { token: 'pink', label: 'Pink', swatch: '#ec4899' },
]

/** Just the tokens — handy for validation / tests / the BE contract mirror. */
export const CARD_COLOR_TOKENS: readonly string[] = CARD_COLOR_OPTIONS.map(
  (o) => o.token,
)

const TOKEN_TO_SWATCH = new Map(CARD_COLOR_OPTIONS.map((o) => [o.token, o.swatch]))

/**
 * The mode-aware surface CSS var the card root must declare so the tint's
 * `color-mix` composites over white in light mode and zinc-800 in dark mode.
 * Applied on the card root via Tailwind arbitrary properties. zinc-800 = #27272a.
 */
export const CARD_COLOR_SURFACE_CLASS =
  '[--qc-card-surface:#ffffff] dark:[--qc-card-surface:#27272a]'

/**
 * normaliseCardColor(value) → a known token, or '' for "no colour".
 *
 * Defensive: a corrupt / legacy / unknown value (raw hex, a removed token,
 * null/undefined) degrades to '' so the card simply renders on the default
 * surface instead of producing an invalid style. Pure: same input → same output.
 */
export function normaliseCardColor(value: string | null | undefined): string {
  if (!value) return ''
  const v = value.trim().toLowerCase()
  return TOKEN_TO_SWATCH.has(v) ? v : ''
}

export interface CardColorStyle {
  /** The card-root background: a soft tint composited over the mode-aware
   *  surface var, or undefined when there is no colour (the caller then keeps
   *  the themed `var(--card-bg, transparent)` default). */
  background?: string
  /** The saturated swatch hue for the chosen token (undefined when none) — the
   *  picker uses it for the "selected" ring; the card can use it for accents. */
  swatch?: string
}

/**
 * resolveCardColorStyle(value) → the inline-style fragment for a card root.
 *
 * We composite the saturated swatch at low alpha OVER `--qc-card-surface`
 * (white in light, zinc-800 in dark — see CARD_COLOR_SURFACE_CLASS). Using
 * `color-mix` means ONE stored token yields a tint that reads correctly in
 * BOTH themes without shipping a light+dark hex pair per colour. An unknown
 * token returns `{}` so the card keeps its default background. If a browser
 * lacks `color-mix` the declaration is simply ignored and the card falls back
 * to its `bg-white dark:bg-zinc-800` class — graceful, never broken.
 */
export function resolveCardColorStyle(
  value: string | null | undefined,
): CardColorStyle {
  const token = normaliseCardColor(value)
  if (!token) return {}
  const swatch = TOKEN_TO_SWATCH.get(token) as string
  return {
    // 18% hue over the surface → a soft pastel in light, a gentle wash in dark;
    // light enough to keep default fg text readable in both modes.
    background: `color-mix(in srgb, ${swatch} 18%, var(--qc-card-surface, #ffffff))`,
    swatch,
  }
}

/** Convenience: the saturated hue for a token, or undefined for none/unknown. */
export function cardColorSwatch(
  value: string | null | undefined,
): string | undefined {
  const token = normaliseCardColor(value)
  return token ? (TOKEN_TO_SWATCH.get(token) as string) : undefined
}
